import { UserRejectedRequestError } from 'viem'

/** A wallet that never answers the switch request would otherwise pin `isPending` true forever. */
const SWITCH_CHAIN_TIMEOUT_MS = 60_000

/** EIP-1193 `userRejectedRequest`. */
const USER_REJECTED_CODE = 4001

/** Guards the cause walk against a cyclic or absurdly deep chain. */
const MAX_CAUSE_DEPTH = 10

/** Decline wording some wallets send without a 4001 code, in either word order. */
const REJECTION_WORDING =
  /user (rejected|denied|cancell?ed)|(rejected|denied|cancell?ed) by( the)? user/i

export const CHAIN_SWITCH_FAILED_MESSAGE = (chainName: string) =>
  `Couldn't switch to ${chainName}. Switch networks in your wallet.`

function isRejectionSignal(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false
  if ((value as { code?: unknown }).code === USER_REJECTED_CODE) return true
  const { message } = value as { message?: unknown }
  return typeof message === 'string' && REJECTION_WORDING.test(message)
}

/**
 * A rejection is the user's own choice, not a failure worth warning about or surfacing.
 *
 * Every wagmi connector's `switchChain` wraps *any* `wallet_addEthereumChain` failure — a
 * disconnected provider, an unsupported method, an RPC error — in `UserRejectedRequestError`,
 * which always carries code 4001 and "User rejected the request." The wrapper therefore proves
 * nothing and is skipped: only a cause below it can tell a decline from a broken wallet.
 */
export function isUserRejectedChainSwitch(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  let node: unknown = error
  for (let depth = 0; node != null && depth < MAX_CAUSE_DEPTH; depth++) {
    if (!(node instanceof UserRejectedRequestError) && isRejectionSignal(node)) return true
    node = (node as { cause?: unknown }).cause
  }
  return false
}

/**
 * Races the wallet's chain switch against a timeout. wagmi exposes no timeout option, and a
 * silent wallet leaves the mutation pending, so `reset()` unlocks the `isPending` guard.
 */
export async function switchChainWithTimeout(
  switchChain: () => Promise<unknown>,
  reset: () => void
): Promise<void> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      switchChain(),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reset()
          reject(new Error(`Chain switch timed out after ${SWITCH_CHAIN_TIMEOUT_MS}ms`))
        }, SWITCH_CHAIN_TIMEOUT_MS)
      })
    ])
  } finally {
    clearTimeout(timeoutId)
  }
}
